/**
 * Follow-up text — the TEXT variant's SMS composer helpers (variants block
 * plan 2, epic §4/§8/§9). Data layer, portable: pure string builders, no DOM.
 *
 * V1 is the composer path — these build `sms:` URLs that open the device's
 * own Messages app prefilled (a real SMS from a real human number, zero
 * backend). Real outbound SMS (Twilio A2P) is the explicit V2.
 *
 * `sms:` body separator (researched 2026-08-13; verify on device): iOS reads
 * `&body=`, Android reads `?body=`, and the `?&body=` compound satisfies both
 * — Android sees a query with `body`, iOS sees its ampersand. Prefill is
 * unreliable with NO recipient, so callers always address the message: the
 * Anon's self-text goes to their own just-shared number (their own thread —
 * the bookmark), the host's follow-up to the Anon's number.
 */

import {
  gateVenueMetadata,
  formatLocationPillPrimary,
  metPreposition,
} from '../../utils/locationFormatters';
import { firstNameOf } from '../../utils/contactUtils';
import { formatPhoneForWhatsApp } from '../../utils/formatters';
import { MONTHS_SHORT } from '../../utils/dateHelpers';

// The recipient is PEER data (the Anon's self-reported value, no write-side
// validation) — strip to digits/+ so a crafted "phone" like
// `+1555&body=EVIL` can't smuggle URL parameters into the host's composer
// (/security-review 2026-08-15).
const cleanRecipient = (phone) => String(phone || '').replace(/[^\d+]/g, '');

/** `sms:` compose URL — recipient + URL-encoded body via the `?&` compound. */
export const buildSmsComposeUrl = (phone, body) => {
  const encoded = body ? `?&body=${encodeURIComponent(body)}` : '';
  return `sms:${cleanRecipient(phone)}${encoded}`;
};

/**
 * WhatsApp compose URL — wa.me carries prefilled text natively (?text=),
 * so a WhatsApp share gets the same one-tap direct send as SMS, no
 * copy-paste step. Number rides the existing wa.me formatter.
 */
export const buildWhatsAppComposeUrl = (phone, body) => {
  // cleanRecipient first — formatPhoneForWhatsApp's E.164 branch returns
  // everything after a leading + verbatim
  const digits = formatPhoneForWhatsApp(cleanRecipient(phone));
  const encoded = body ? `?text=${encodeURIComponent(body)}` : '';
  return `https://wa.me/${digits}${encoded}`;
};

/**
 * The channel the host's follow-up goes to (device pass 1j — the Anon may
 * have shared Instagram/email instead of a number): a phone wins outright
 * (direct SMS beats copy-paste); otherwise the record's contactType names
 * the shared channel (the TEXT flow shares exactly one). Returns
 * { type, value } or null (no prompt without a channel).
 */
export const pickDmChannel = (anonData) => {
  if (!anonData) return null;
  if (anonData.phone) return { type: 'phone', value: anonData.phone };
  const type = anonData.contactType;
  if (type && anonData[type]) return { type, value: anonData[type] };
  return null;
};

/**
 * The Anon's self-text body (user copy, 2026-08-13): "{FirstName}'s card on
 * Royale" above the link — the bookmark names itself in the thread; the link
 * sits on its own line so it unfurls cleanly.
 */
export const buildSelfTextBody = (hostName, link) => {
  const first = firstNameOf(hostName);
  return `${first ? `${first}'s card` : 'Card'} on Royale\n${link}`;
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The prose time word, device-local (the host composes on the device that
// stamped the meeting): tonight / today / yesterday / "on Friday" (<7d) /
// "on Aug 8". Falls to 'recently' on missing/invalid dates.
const followUpTimeWord = (meetingDate, now) => {
  if (!meetingDate) return 'recently';
  const met = new Date(meetingDate);
  if (Number.isNaN(met.getTime())) return 'recently';
  if (met.toDateString() === now.toDateString()) {
    return met.getHours() >= 17 ? 'tonight' : 'today';
  }
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (met.toDateString() === yesterday.toDateString()) return 'yesterday';
  if (now.getTime() - met.getTime() < 7 * 24 * 60 * 60 * 1000) {
    return `on ${DAY_NAMES[met.getDay()]}`;
  }
  return `on ${MONTHS_SHORT[met.getMonth()]} ${met.getDate()}`;
};

/**
 * The prefilled follow-up draft (epic §4/§9): "Hey Mark it's Bri, great to
 * see you tonight at the Hoxton". Auto-written from moment data, always
 * editable in the overlay — never sent silently.
 *
 * Place rides the rails: the GATED clone (a skipped venue falls to the
 * neighborhood — Skip ≡ picking the neighborhood) + metPreposition (at/in).
 * A place-less moment drops the clause entirely; missing names degrade
 * clause-by-clause rather than emitting "Hey undefined".
 *
 * `now` is injectable for tests; callers omit it.
 */
export const buildFollowUpDraft = ({
  anonName,
  hostName,
  connectionMetadata,
  now = new Date(),
}) => {
  const anonFirst = firstNameOf(anonName);
  const hostFirst = firstNameOf(hostName);
  const meta = gateVenueMetadata(connectionMetadata);
  const place = formatLocationPillPrimary(meta);
  const placeClause = place ? ` ${metPreposition(place, meta)} ${place}` : '';
  const seeYou = `great to see you ${followUpTimeWord(connectionMetadata?.meetingDate, now)}${placeClause}`;

  if (anonFirst && hostFirst) return `Hey ${anonFirst} it's ${hostFirst}, ${seeYou}`;
  if (hostFirst) return `Hey, it's ${hostFirst} — ${seeYou}`;
  return `${seeYou.charAt(0).toUpperCase()}${seeYou.slice(1)}`;
};
